export interface SimilarProductRow {
  id: string
  empresaId: string
  empresaNome: string
  produtoNovoId: string
  produtoNovoNome: string
  produtoSimilarId: string
  produtoSimilarNome: string
  demanda: string
  dataInicio: string // stored as yyyy-MM-dd for date inputs
  dataFim: string // stored as yyyy-MM-dd for date inputs
  usuario: string
  _lineNumber: number
  errors: string[]
}

// ─── internals ───────────────────────────────────────────────────────────────

function normalizeStr(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

function parseCSVLine(line: string, sep: string): string[] {
  const cols: string[] = []
  let cur = ''
  let inQ = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      inQ = !inQ
    } else if (ch === sep && !inQ) {
      cols.push(cur.trim())
      cur = ''
    } else {
      cur += ch
    }
  }
  cols.push(cur.trim())
  return cols
}

/** Maps a raw CSV header to a SimilarProductRow field key */
function mapHeader(h: string): keyof SimilarProductRow | null {
  const n = normalizeStr(h)

  // id do registro
  if (n === 'id') return 'id'

  // Empresa - Id
  if (n === 'empresa - id' || n === 'empresaid' || n === 'empresa id') {
    return 'empresaId'
  }
  // Empresa - Nome
  if (
    n === 'empresa - nome' ||
    n === 'empresanome' ||
    n === 'empresa nome' ||
    n === 'empresa'
  ) {
    return 'empresaNome'
  }

  // Produto Novo - Id
  if (
    n === 'produto novo - id' ||
    n === 'produtonovoid' ||
    n === 'produto novo id' ||
    n === 'produto novo - id' ||
    n === 'produto novo'
  ) {
    return 'produtoNovoId'
  }
  // Produto Novo - Nome
  if (
    n === 'produto novo - nome' ||
    n === 'produtonovonome' ||
    n === 'produto novo nome' ||
    n === 'nome produto novo'
  ) {
    return 'produtoNovoNome'
  }

  // Produto Similar - Id
  if (
    n === 'produto similar - id' ||
    n === 'produtosimilarid' ||
    n === 'produto similar id' ||
    n === 'similar - id'
  ) {
    return 'produtoSimilarId'
  }
  // Produto Similar - Nome
  if (
    n === 'produto similar - nome' ||
    n === 'produtosimilarnome' ||
    n === 'produto similar nome' ||
    n === 'similar - nome' ||
    n === 'similar'
  ) {
    return 'produtoSimilarNome'
  }

  // Percentual Demanda Absorvida
  if (
    n.includes('percentual') ||
    n.includes('demanda') ||
    n === '%' ||
    n === 'percentualdemandaabsorvida'
  ) {
    return 'demanda'
  }

  // Data - Inicio
  if (
    n === 'data - inicio' ||
    n.includes('datainicio') ||
    n === 'data inicio' ||
    n === 'inicio'
  ) {
    return 'dataInicio'
  }
  // Data - Fim
  if (
    n === 'data - fim' ||
    n.includes('datafim') ||
    n === 'data fim' ||
    n === 'fim'
  ) {
    return 'dataFim'
  }

  // Usuario
  if (
    n === 'usuario' ||
    n === 'configuracao - nome' ||
    n === 'configuração - nome'
  ) {
    return 'usuario'
  }

  return null
}

/**
 * Converts a date string (dd/MM/yyyy or yyyy-MM-dd) to yyyy-MM-dd for
 * use in <input type="date">.
 */
function toInputDate(raw: string): string {
  const s = raw.trim()
  if (!s) return ''
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (slash) {
    const [, d, m, y] = slash
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  return s
}

/**
 * Converts yyyy-MM-dd (from date input) to dd/MM/yyyy for the CSV
 * that the backend expects.
 */
function toCsvDate(s: string): string {
  if (!s) return ''
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (iso) {
    const [, y, m, d] = iso
    return `${d}/${m}/${y}`
  }
  return s
}

// ─── exports ─────────────────────────────────────────────────────────────────

export function parseSimilarProductCSV(text: string): {
  rows: SimilarProductRow[]
  parseError: string | null
} {
  let t = text
  if (t.charCodeAt(0) === 0xfeff) t = t.slice(1)

  const lines = t.split(/\r?\n/).filter(l => l.trim())
  if (lines.length < 2) {
    return { rows: [], parseError: 'Arquivo vazio ou sem registros.' }
  }

  const sep = lines[0].includes(';') ? ';' : ','
  const rawHeaders = parseCSVLine(lines[0], sep)

  const fieldMap: Record<number, keyof SimilarProductRow> = {}
  rawHeaders.forEach((h, i) => {
    const field = mapHeader(h)
    if (field && !(field in Object.values(fieldMap))) fieldMap[i] = field
  })

  // Require at least the two key ID columns
  const required: Array<keyof SimilarProductRow> = [
    'produtoNovoId',
    'produtoSimilarId'
  ]
  const mappedFields = Object.values(fieldMap)
  const missing = required.filter(f => !mappedFields.includes(f))
  if (missing.length > 0) {
    const labels: Record<string, string> = {
      produtoNovoId: 'Produto Novo - Id',
      produtoSimilarId: 'Produto Similar - Id'
    }
    return {
      rows: [],
      parseError: `Colunas obrigatórias não encontradas: ${missing
        .map(f => labels[f])
        .join(', ')}. Verifique o cabeçalho do arquivo.`
    }
  }

  const rows: SimilarProductRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i], sep)
    const row: SimilarProductRow = {
      id: '',
      empresaId: '',
      empresaNome: '',
      produtoNovoId: '',
      produtoNovoNome: '',
      produtoSimilarId: '',
      produtoSimilarNome: '',
      demanda: '',
      dataInicio: '',
      dataFim: '',
      usuario: '',
      _lineNumber: i + 1,
      errors: []
    }
    Object.entries(fieldMap).forEach(([colIdx, field]) => {
      const val = (cols[Number(colIdx)] ?? '').trim()
      if (field === 'dataInicio' || field === 'dataFim') {
        ;(row as any)[field] = toInputDate(val)
      } else {
        ;(row as any)[field] = val
      }
    })
    rows.push(row)
  }

  return { rows, parseError: null }
}

/**
 * Mirrors the backend's ValidarColunas() + frontend 1:1 rule.
 */
export function validateRows(rows: SimilarProductRow[]): SimilarProductRow[] {
  // ── field-level (mirrors ValidarColunas) ──────────────────────────────────
  const firstPass = rows.map(row => {
    const errors: string[] = []

    if (!row.empresaId.trim()) {
      errors.push('Coluna [Empresa - Id] deve ser preenchida.')
    }
    if (!row.empresaNome.trim()) {
      errors.push('Coluna [Empresa - Nome] deve ser preenchida.')
    }
    if (!row.produtoNovoId.trim()) {
      errors.push('Coluna [Produto Novo - Id] deve ser preenchida.')
    }
    if (!row.produtoNovoNome.trim()) {
      errors.push('Coluna [Produto Novo - Nome] deve ser preenchida.')
    }
    if (!row.produtoSimilarId.trim()) {
      errors.push('Coluna [Produto Similar - Id] deve ser preenchida.')
    }
    if (!row.produtoSimilarNome.trim()) {
      errors.push('Coluna [Produto Similar - Nome] deve ser preenchida.')
    }

    // Dates: validate as yyyy-MM-dd (stored after normalisation)
    if (!row.dataInicio.trim() || isNaN(Date.parse(row.dataInicio))) {
      errors.push('Coluna [Data - Inicio] em formato inválido.')
    }
    if (!row.dataFim.trim() || isNaN(Date.parse(row.dataFim))) {
      errors.push('Coluna [Data - Fim] em formato inválido.')
    }

    if (
      row.dataInicio &&
      row.dataFim &&
      !isNaN(Date.parse(row.dataInicio)) &&
      !isNaN(Date.parse(row.dataFim)) &&
      new Date(row.dataFim) < new Date(row.dataInicio)
    ) {
      errors.push(
        'Coluna [Data - Fim] deve ser maior ou igual à Data de Início.'
      )
    }

    return { ...row, errors }
  })

  // ── 1:1 rule (frontend only) ──────────────────────────────────────────────
  const groupMap: Record<string, Set<string>> = {}
  firstPass.forEach(row => {
    const key = row.produtoNovoId.trim()
    const sim = row.produtoSimilarId.trim()
    if (!key || !sim) return
    if (!groupMap[key]) groupMap[key] = new Set()
    groupMap[key].add(sim)
  })

  return firstPass.map(row => {
    const key = row.produtoNovoId.trim()
    const group = groupMap[key]
    if (group && group.size > 1) {
      const msg =
        'Violação 1:1: Produto Novo mapeado para mais de um Produto Similar no arquivo.'
      if (!row.errors.includes(msg)) {
        return { ...row, errors: [...row.errors, msg] }
      }
    }
    return row
  })
}

/**
 * Rebuilds the CSV in the format the backend expects.
 * Dates are output as dd/MM/yyyy.
 */
export function buildCSV(rows: SimilarProductRow[]): string {
  const header =
    'Id;Empresa - Id;Empresa - Nome;Produto Novo - Id;Produto Novo - Nome;Produto Similar - Id;Produto Similar - Nome;PercentualDemandaAbsorvida;Data - Inicio;Data - Fim;Usuario'
  const dataRows = rows.map(r =>
    [
      r.id,
      r.empresaId,
      r.empresaNome,
      r.produtoNovoId,
      r.produtoNovoNome,
      r.produtoSimilarId,
      r.produtoSimilarNome,
      r.demanda,
      toCsvDate(r.dataInicio),
      toCsvDate(r.dataFim),
      r.usuario
    ].join(';')
  )
  return '\uFEFF' + [header, ...dataRows].join('\r\n')
}
